import React from "react";
import clsx from "clsx";
import styles from "./Button.module.scss";
import { Icon, IconName } from "../Icon/Icon";
import { isPresent } from "../utils/checks";

export type ButtonVariant =
  | "primary"
  | "accent"
  | "secondary"
  | "text"
  | "inline-text"
  | "elevated"
  | "link";

export type ButtonSize = "regular" | "small";
interface ButtonBaseProps {
  loading?: boolean;
  "data-content-piece"?: string;
  noWrap?: boolean;
  variant?: ButtonVariant;
  inverted?: boolean;
  size?: ButtonSize;
  mobileSize?: ButtonSize;
  full?: boolean;
  disabled?: boolean;
  className?: string;
  dataContentPiece: string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataContentParams?: Record<string, any>;

  /** icon name. */
  iconName?: IconName;
  iconPosition?: "left" | "right";
}

export interface ButtonAsLinkProps
  extends React.DetailedHTMLProps<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      HTMLAnchorElement
    >,
    ButtonBaseProps {
  renderAs?: "a";
}

export interface ButtonAsButtonProps
  extends React.DetailedHTMLProps<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      HTMLButtonElement
    >,
    ButtonBaseProps {
  renderAs: "button";
}

export interface ButtonAsDivProps
  extends React.DetailedHTMLProps<
      React.ButtonHTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >,
    ButtonBaseProps {
  renderAs: "div";
}

export type ButtonProps =
  | ButtonAsLinkProps
  | ButtonAsButtonProps
  | ButtonAsDivProps;

const defaultRenderAsFromProps = (props: ButtonProps): React.ElementType => {
  if (isPresent(props.renderAs)) return props.renderAs;
  if ("href" in props && isPresent(props.href)) return "a";
  return "button";
};

export const Button: React.FC<React.PropsWithChildren<ButtonProps>> = (
  props
) => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderAs,
    loading,
    noWrap,
    variant,
    inverted,
    size,
    mobileSize,
    full,
    disabled,
    className,
    iconName,
    iconPosition = "right",
    children,
    dataContentPiece,
    dataContentParams,
    ...otherProps
  } = props;
  const ButtonComponent = defaultRenderAsFromProps(props);

  const overrideDataContentPiece =
    dataContentPiece || props["data-content-piece"];
  const overrideDataContentParams =
    dataContentParams || props["data-content-params"];

  const validChildren = React.Children.toArray(children).filter(
    (child) => React.isValidElement(child) || typeof child === "string"
  );

  const buttonClassNames = generateClassNames({
    className,
    size,
    mobileSize,
    variant,
    inverted,
    loading,
    full,
    iconName,
    iconPosition,
    noWrap,
    hasChildren: validChildren.length > 0
  });

  return (
    <ButtonComponent
      disabled={disabled}
      {...otherProps}
      className={buttonClassNames}
      data-content-piece={overrideDataContentPiece}
      data-content-params={
        overrideDataContentParams
          ? JSON.stringify(overrideDataContentParams)
          : undefined
      }
    >
      {iconName && iconPosition == "left" && (
        <Icon
          name={iconName}
          size="1.5x"
          wrapperClassName={styles.icon}
          padding={false}
          ariaHidden
        />
      )}
      {children && <span>{children}</span>}
      {iconName && iconPosition == "right" && (
        <Icon
          name={iconName}
          size="1.5x"
          wrapperClassName={styles.icon}
          padding={false}
          ariaHidden
        />
      )}
    </ButtonComponent>
  );
};

function generateClassNames(
  args: Pick<
    ButtonBaseProps,
    | "className"
    | "size"
    | "mobileSize"
    | "variant"
    | "inverted"
    | "loading"
    | "full"
    | "iconName"
    | "iconPosition"
    | "noWrap"
  > & { hasChildren: boolean }
): string {
  const {
    className,
    size = "regular",
    mobileSize,
    variant = "primary",
    inverted = false,
    loading,
    full = false,
    iconName,
    iconPosition = "right",
    hasChildren,
    noWrap
  } = args;

  const btnClass = clsx(styles.button, className, {
    [styles.sizeSmall]: size == "small",
    [styles.mobileSizeSmall]: size != "small" && mobileSize == "small",

    [styles.variantPrimary]: variant == "primary",
    [styles.variantAccent]: variant == "accent",
    [styles.variantSecondary]: variant == "secondary",
    [styles.variantText]: variant == "text",
    [styles.variantInlineText]: variant == "inline-text",
    [styles.variantElevated]: variant == "elevated",
    [styles.variantLink]: variant == "link",

    [styles.inverted]: inverted,
    [styles.iconRight]: iconName && iconPosition == "right",
    [styles.iconLeft]: iconName && iconPosition == "left",
    [styles.iconOnly]: iconName && !hasChildren,

    [styles.noWrap]: noWrap,
    [styles.full]: full,
    [styles.loading]: loading
  });
  return btnClass;
}
